import { client } from "./claims";
import { Page } from "../components/caf-workflows/workflowTypes";
import {
  IntakeControl,
  AnyRunDetail,
  RunListItem,
  RunDetail,
  WorkflowSnapshot,
  QueueItem,
} from "../components/caf-workflows/workflowRunTypes";
const base = "/api/admin/caf-workflows";
const runs = "/api/admin/caf-workflow-runs";
const runPath = (id: string) => `${runs}/${encodeURIComponent(id)}`;
export interface EffectCapacity {
  slot_limit: number;
  revision: number;
  min_limit: number;
  max_limit: number;
  default_limit: number;
  occupied: number;
  active_runs: number;
  unresolved_runs: number;
  occupied_children: number;
  legacy_slot_limit: number;
}
export const getEffectCapacity = (signal?: AbortSignal) =>
  client
    .get<EffectCapacity>(
      `${base}/effect-capacity`,
      readConfig(undefined, signal),
    )
    .then((r) => r.data);
export const setEffectCapacity = (
  slot_limit: number,
  expected_revision: number,
  reason: string,
) =>
  client
    .post<EffectCapacity>(`${base}/effect-capacity`, {
      slot_limit,
      expected_revision,
      reason,
    })
    .then((r) => r.data);
// Runtime Axios supports AbortSignal; the app's legacy declaration omits it.
const readConfig = (
  params: Record<string, unknown> | undefined,
  signal?: AbortSignal,
) => ({ params, signal });
export const getWorkflowSnapshot = (days: number, signal?: AbortSignal) =>
  client
    .get<WorkflowSnapshot>(`${base}/oversight`, readConfig({ days }, signal))
    .then((r) => r.data);
export const getIntakeControl = (signal?: AbortSignal) =>
  client
    .get<IntakeControl>(`${base}/intake-control`, readConfig(undefined, signal))
    .then((r) => r.data);
export const setIntakeControl = (
  mode: string,
  expected_epoch: number,
  reason: string,
) =>
  client
    .post<IntakeControl>(`${base}/intake-control`, {
      mode,
      expected_epoch,
      reason,
    })
    .then((r) => r.data);
export const listRuns = (
  params: {
    cursor?: string;
    status?: string;
    pattern_id?: string;
    pattern_version?: number;
  },
  signal?: AbortSignal,
) =>
  client
    .get<Page<RunListItem>>(runs, readConfig(params, signal))
    .then((r) => r.data);
export const getRun = (id: string, cursor?: string, signal?: AbortSignal) =>
  client
    .get<AnyRunDetail>(runPath(id), readConfig({ cursor, limit: 25 }, signal))
    .then((r) => r.data);
export const getRunWaits = (id: string, signal?: AbortSignal) =>
  client
    .get<{ items: import('../components/caf-workflows/workflowRunTypes').RunDetail["waits"] }>(
      `${runPath(id)}/waits`,
      readConfig(undefined, signal),
    )
    .then((r) => r.data.items);
export const controlRun = (
  id: string,
  operation: "pause" | "resume" | "reconcile" | "retry",
  expected_revision: number,
  reason: string,
) =>
  client
    .post<RunDetail>(`${runPath(id)}/${operation}`, {
      expected_revision,
      reason,
    })
    .then((r) => r.data);
export const restartWait = (
  id: string,
  node: string,
  expected_revision: number,
  reason: string,
  idempotency_key: string,
) =>
  client
    .post<RunDetail>(
      `${runPath(id)}/waits/${encodeURIComponent(node)}/restart`,
      { expected_revision, reason, idempotency_key },
    )
    .then((r) => r.data);
export const recordContact = (
  id: string,
  body: {
    received_at: string;
    channel: string;
    reason: string;
    idempotency_key: string;
  },
) => client.post(`${runPath(id)}/responses`, body).then((r) => r.data);
export const getWorkflowQueue = (
  params: { queue: string; days: number; cursor?: string; as_of?: string },
  signal?: AbortSignal,
) =>
  client
    .get<Page<QueueItem> & { authority: string }>(
      `${base}/oversight/queue`,
      readConfig({ ...params, limit: 25 }, signal),
    )
    .then((r) => r.data);
export const runError = (error: unknown) => {
  const e = error as {
    response?: { data?: { code?: string; error?: string } };
    message?: string;
  };
  return (
    e?.response?.data?.code ||
    e?.response?.data?.error ||
    e?.message ||
    "Workflow request failed"
  );
};
